/***************************************************************************

    SEO.tsx
    -------------------------
    last Update          : January 15, 2025
    by                   : quentin.savoye@cil4sys.com
    copyright            : (C) 2025 by CIL4Sys Engineering 

***************************************************************************/


import { useStaticQuery, graphql } from "gatsby"
import { useTranslation, useI18next } from "gatsby-plugin-react-i18next"
import React from "react"
import { Helmet } from "react-helmet-async"

import { getGeoStructuredData, getOrganizationSameAs } from "../lib/geoSchema"
import { PageSeoLang } from "../lib/pageSeo"

interface SiteData {
	siteMetadata: {
		title: string
		description: string
		keywords: string
		url: string
		siteUrl: string
		author: string
		image: string
		organization: {
			name: string
			url: string
			logo: string
			address?: {
				streetAddress?: string
				addressLocality?: string
				addressRegion?: string
				postalCode?: string
				addressCountry?: string
			}
			contactPoint?: {
				contactType?: string
				email?: string
			}
		}
	}
}
interface QueryData {
	site: SiteData
}

interface Props {
	description: string
	language: string
	metadata: Array<{ name: string; content: string; property?: string }>
	title: string
	keywords: string
	image?: string
	canonicalUrl?: string
	noindex?: boolean
	nofollow?: boolean
	pageKey?: string
	site: SiteData
}

class SEOClassComponent extends React.Component<Props> {
	constructor(aProps: Props) {
		super(aProps)
	}

	private getCanonicalUrl(): string {
		if (this.props.canonicalUrl) {
			return this.props.canonicalUrl
		}
		const baseUrl = (this.props.site?.siteMetadata?.siteUrl || "").replace(/\/$/, "")
		return `${baseUrl}/`
	}

	private getAlternateLanguages(): Array<{ hreflang: string; href: string }> {
		const baseUrl = (this.props.site?.siteMetadata?.siteUrl || "").replace(/\/$/, "")
		const currentUrl = this.getCanonicalUrl()
		const currentPath = currentUrl.replace(baseUrl, "").replace(/^\/[a-z]{2}\//, "/").replace(/^\/[a-z]{2}$/, "/") || "/"
		const languages = ["en", "fr"]
		return languages.map((lang) => {
			const normalized = currentPath === "/" ? "/" : currentPath.replace(/\/?$/, "/")
			const langPath =
				lang === "en" ? normalized : `/fr${normalized === "/" ? "/" : normalized}`
			const hrefPath = langPath === "/" ? "/" : langPath.replace(/\/?$/, "/")
			return {
				hreflang: lang,
				href: `${baseUrl}${hrefPath === "//" ? "/" : hrefPath}`,
			}
		})
	}

	private getStructuredData(): Array<Record<string, unknown>> {
		const organization = this.props.site?.siteMetadata?.organization
		const siteUrl = this.props.site?.siteMetadata?.siteUrl || ""
		const canonicalUrl = this.getCanonicalUrl()

		// Données structurées pour l'organisation (essentiel pour Google et assistants IA)
		const organizationData: Record<string, unknown> = {
			"@context": "https://schema.org",
			"@type": "Organization",
			name: organization?.name || this.props.site?.siteMetadata?.title,
			url: organization?.url || siteUrl,
			logo: organization?.logo || `${siteUrl}/logo.png`,
			description: this.props.description,
			foundingDate: "2015", // CIL4Sys est né en 2015
			...(organization?.address && {
				address: {
					"@type": "PostalAddress",
					...(organization.address.streetAddress && { streetAddress: organization.address.streetAddress }),
					...(organization.address.addressLocality && { addressLocality: organization.address.addressLocality }),
					...(organization.address.addressRegion && { addressRegion: organization.address.addressRegion }),
					...(organization.address.postalCode && { postalCode: organization.address.postalCode }),
					...(organization.address.addressCountry && { addressCountry: organization.address.addressCountry }),
				},
			}),
			...(organization?.contactPoint && {
				contactPoint: {
					"@type": "ContactPoint",
					...(organization.contactPoint.contactType && { contactType: organization.contactPoint.contactType }),
					...(organization.contactPoint.email && { email: organization.contactPoint.email }),
				},
			}),
			sameAs: getOrganizationSameAs(),
		}

		// Données structurées pour le site web (améliore l'indexation)
		// No SearchAction: site has no /search endpoint (false rich-result signal)
		const websiteData: Record<string, unknown> = {
			"@context": "https://schema.org",
			"@type": "WebSite",
			name: this.props.site?.siteMetadata?.title || "CIL4Sys Engineering",
			url: siteUrl,
			description: this.props.description,
			inLanguage: [this.props.language === "fr" ? "fr-FR" : "en-US", this.props.language === "fr" ? "en-US" : "fr-FR"],
			publisher: {
				"@type": "Organization",
				name: organization?.name || "CIL4Sys Engineering",
			},
		}

		// Données structurées pour la page actuelle (améliore les featured snippets)
		const webpageData: Record<string, unknown> = {
			"@context": "https://schema.org",
			"@type": "WebPage",
			url: canonicalUrl,
			name: this.props.title,
			description: this.props.description,
			inLanguage: this.props.language === "fr" ? "fr-FR" : "en-US",
			isPartOf: {
				"@type": "WebSite",
				name: this.props.site?.siteMetadata?.title,
				url: siteUrl,
			},
			about: {
				"@type": "Organization",
				name: organization?.name || "CIL4Sys Engineering",
			},
			primaryImageOfPage: {
				"@type": "ImageObject",
				url: this.props.image || this.props.site?.siteMetadata?.image || `${siteUrl}/og-image.jpg`,
			},
		}

		const siteUrlClean = siteUrl.replace(/\/$/, "")
		const pageLang: PageSeoLang = this.props.language === "fr" ? "fr" : "en"
		const geoData = this.props.pageKey
			? getGeoStructuredData(this.props.pageKey, pageLang, siteUrlClean)
			: []

		return [organizationData, websiteData, webpageData, ...geoData]
	}

	public override render(): React.ReactNode {
		const siteUrl = this.props.site?.siteMetadata?.siteUrl || ""
		const imageUrl = this.props.image || this.props.site?.siteMetadata?.image || `${siteUrl}/og-image.jpg`
		const canonicalUrl = this.getCanonicalUrl()
		const alternateLanguages = this.getAlternateLanguages()
		const structuredData = this.getStructuredData()

		const baseMetaTags: Array<{ name?: string; property?: string; content: string }> = [
			// Basic meta tags
			{
				name: `description`,
				content: this.props.description,
			},
			{
				name: `keywords`,
				content: this.props.keywords,
			},
			{
				name: `author`,
				content: this.props.site?.siteMetadata?.author || "CIL4Sys Engineering",
			},
			// Métadonnées pour les assistants IA
			{
				name: `abstract`,
				content: this.props.description,
			},
			{
				name: `summary`,
				content: this.props.description,
			},
			// Open Graph tags
			{
				property: `og:title`,
				content: this.props.title,
			},
			{
				property: `og:description`,
				content: this.props.description,
			},
			{
				property: `og:type`,
				content: `website`,
			},
			{
				property: `og:url`,
				content: canonicalUrl,
			},
			{
				property: `og:image`,
				content: imageUrl,
			},
			{
				property: `og:image:width`,
				content: `1200`,
			},
			{
				property: `og:image:height`,
				content: `630`,
			},
			{
				property: `og:locale`,
				content: this.props.language === "fr" ? `fr_FR` : `en_US`,
			},
			{
				property: `og:site_name`,
				content: this.props.site?.siteMetadata?.title || "CIL4Sys",
			},
			// Twitter Card tags
			{
				name: `twitter:card`,
				content: `summary_large_image`,
			},
			{
				name: `twitter:title`,
				content: this.props.title,
			},
			{
				name: `twitter:description`,
				content: this.props.description,
			},
			{
				name: `twitter:image`,
				content: imageUrl,
			},
		]

		const robotsMeta =
			this.props.noindex || this.props.nofollow
				? [
						{
							name: `robots`,
							content: `${this.props.noindex ? "noindex" : ""}${this.props.noindex && this.props.nofollow ? "," : ""}${this.props.nofollow ? "nofollow" : ""}`,
						},
				  ]
				: []

		const metaTags: Array<{ name?: string; property?: string; content: string }> = [
			...baseMetaTags,
			...robotsMeta,
			...this.props.metadata.filter((meta): meta is { name?: string; property?: string; content: string } => Boolean(meta && meta.content)),
		]

		return (
			<>
				<Helmet htmlAttributes={{ lang: this.props.language }} meta={metaTags}>
					<title>{this.props.title}</title>
					<link rel="canonical" href={canonicalUrl} />
					{alternateLanguages.map((alt) => (
						<link key={alt.hreflang} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
					))}
					<link rel="alternate" hrefLang="x-default" href={alternateLanguages.find((alt) => alt.hreflang === "en")?.href || canonicalUrl} />
				</Helmet>
				{structuredData.map((data: Record<string, unknown>, index: number) => (
					<Helmet key={index}>
						<script type="application/ld+json">{JSON.stringify(data)}</script>
					</Helmet>
				))}
			</>
		)
	}
}

interface SEOComponentProps {
	description?: string
	lang?: string
	meta?: Array<{ name: string; content: string; property?: string }>
	title?: string
	keywords?: string
	image?: string
	canonicalUrl?: string
	noindex?: boolean
	nofollow?: boolean
	pageKey?: string
}

const seoComponent = ({
	description: aDescription = "",
	lang: aLanguage,
	meta: someMetadata = [],
	title: aTitle,
	keywords: aKeywords,
	image: anImage,
	canonicalUrl: aCanonicalUrl,
	noindex: aNoindex,
	nofollow: aNofollow,
	pageKey: aPageKey,
}: SEOComponentProps): JSX.Element => {
	const { site } = useStaticQuery<QueryData>(graphql`
		query {
			site {
				siteMetadata {
					title
					description
					keywords
					url
					siteUrl
					author
					image
					organization {
						name
						url
						logo
						address {
							streetAddress
							addressLocality
							addressRegion
							postalCode
							addressCountry
						}
						contactPoint {
							contactType
							email
						}
					}
				}
			}
		}
	`)
	const { t } = useTranslation()
	const { language, path } = useI18next()

	const currentLanguage = aLanguage || language || `en`
	const keywords = aKeywords || site.siteMetadata.keywords
	const metaDescription = aDescription || site.siteMetadata.description
	const defaultTitle = aTitle || t("Site title") || site.siteMetadata.title

	// Canonical always from siteUrl + i18n path (SSR-safe, no window)
	const baseUrl = (site.siteMetadata.siteUrl || "").replace(/\/$/, "")
	const currentPath = path || "/"
	const canonicalUrl = aCanonicalUrl || `${baseUrl}${currentPath.startsWith("/") ? currentPath : `/${currentPath}`}`

	return (
		<SEOClassComponent
			title={defaultTitle}
			description={metaDescription}
			language={currentLanguage}
			metadata={someMetadata}
			keywords={keywords}
			image={anImage}
			canonicalUrl={canonicalUrl}
			noindex={aNoindex}
			nofollow={aNofollow}
			pageKey={aPageKey}
			site={site}
		/>
	)
}

seoComponent.defaultProps = {
	lang: `en`,
	meta: [],
	description: ``,
	noindex: false,
	nofollow: false,
}

export default seoComponent
